import type { InsertedPiece, Piece } from '@/types';
import { isPieceId } from './pieceId';

export type StatementSegment = StatementTextSegment | StatementPieceSegment;

export interface StatementTextSegment {
	type: 'text';
	content: string;
}

export interface StatementPieceSegment {
	type: 'piece';
	content: string;
	pieceId?: string;
	number?: number;
	name?: string;
}

export interface ResolvedStatementPiece {
	id: string;
	title: string;
	piece?: Piece;
}

interface StatementMatch {
	start: number;
	end: number;
	segment: StatementPieceSegment;
}

const DOCUMENT_TAG_REGEX = /<document\b([^>]*)>([\s\S]*?)<\/document>/gi;
const DOCUMENT_ID_ATTRIBUTE_REGEX = /(?:^|\s)id\s*=\s*(?:"([^"]+)"|'([^']+)')/i;
const LEGACY_PIECE_REGEX = /\*\*Pi[eè]ce\s*n[°ºo]\s*(\d+)\s*[:\-]\s*([\s\S]+?)\*\*/gi;

const uniquePieces = (allPieces: Piece[], visiblePieces: Piece[]): Piece[] => {
	const byId = new Map<string, Piece>();
	for (const piece of [...allPieces, ...visiblePieces]) {
		byId.set(piece.id, piece);
	}
	return [...byId.values()];
};

export const parseStatementOfFactsSegments = (text: string): StatementSegment[] => {
	const matches: StatementMatch[] = [];

	for (const match of text.matchAll(DOCUMENT_TAG_REGEX)) {
		const idMatch = match[1].match(DOCUMENT_ID_ATTRIBUTE_REGEX);
		const pieceId = idMatch?.[1] ?? idMatch?.[2];
		if (!pieceId || match.index === undefined) continue;

		matches.push({
			start: match.index,
			end: match.index + match[0].length,
			segment: {
				type: 'piece',
				content: match[2],
				pieceId,
				name: match[2]
			}
		});
	}

	for (const match of text.matchAll(LEGACY_PIECE_REGEX)) {
		if (match.index === undefined) continue;

		matches.push({
			start: match.index,
			end: match.index + match[0].length,
			segment: {
				type: 'piece',
				content: match[0].replace(/\*\*/g, ''),
				number: Number.parseInt(match[1], 10),
				name: match[2]
			}
		});
	}

	if (matches.length === 0) {
		return text ? [{ type: 'text', content: text }] : [];
	}

	matches.sort((a, b) => a.start - b.start || b.end - a.end);

	const segments: StatementSegment[] = [];
	let lastIndex = 0;

	for (const match of matches) {
		if (match.start < lastIndex) continue;
		if (match.start > lastIndex) {
			segments.push({ type: 'text', content: text.slice(lastIndex, match.start) });
		}
		segments.push(match.segment);
		lastIndex = match.end;
	}

	if (lastIndex < text.length) {
		segments.push({ type: 'text', content: text.slice(lastIndex) });
	}

	return segments;
};

export const resolveStatementPiece = (
	segment: StatementPieceSegment,
	allPieces: Piece[],
	pieces: Piece[],
	insertedPieces: InsertedPiece[]
): ResolvedStatementPiece | null => {
	const candidates = uniquePieces(allPieces, pieces);

	if (segment.pieceId) {
		const piece = candidates.find((p) => p.id === segment.pieceId);
		// An unknown malformed ID was mistyped by the model: the caller inserts the citation as plain text
		if (!piece && !isPieceId(segment.pieceId)) {
			return null;
		}
		return {
			id: segment.pieceId,
			title: piece?.name ?? segment.name ?? segment.content,
			piece
		};
	}

	if (segment.number === undefined) return null;

	const pieceByPappersNumber = candidates.find((p) => p.number === segment.number);
	if (pieceByPappersNumber) {
		return {
			id: pieceByPappersNumber.id,
			title: pieceByPappersNumber.name,
			piece: pieceByPappersNumber
		};
	}

	const insertedPiece = insertedPieces.find((ip) => ip.number === segment.number);
	if (!insertedPiece) return null;

	const pieceByInsertedNumber = candidates.find((p) => p.id === insertedPiece.pieceId);
	return {
		id: insertedPiece.pieceId,
		title: pieceByInsertedNumber?.name ?? segment.name ?? segment.content,
		piece: pieceByInsertedNumber
	};
};
